// The text popover: sits over the stage, can be dragged by its handle, and on small screens
// folds everything but the text field behind a native <details>.

import { useRef, useState, type PointerEvent } from 'react';
import type { TextObject } from './design';
import type { Strings } from './strings';
import { FONTS, PALETTE } from './text';

interface Props {
  t: Strings;
  text: TextObject;
  sizeMin: number;
  sizeMax: number;
  onChange(t: TextObject): void;
  onDone(): void;
}

const small = () => window.matchMedia('(max-width: 640px)').matches;

export function TextTools({ t, text, sizeMin, sizeMax, onChange, onDone }: Props) {
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number; px: number; py: number } | null>(null);
  const onPointerDown = (e: PointerEvent<HTMLButtonElement>) => {
    drag.current = { x: e.clientX, y: e.clientY, px: pos.x, py: pos.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: PointerEvent<HTMLButtonElement>) => {
    if (drag.current) setPos({ x: drag.current.px + e.clientX - drag.current.x, y: drag.current.py + e.clientY - drag.current.y });
  };
  const onPointerUp = () => { drag.current = null; };

  return (
    <div className="texttools" style={{ transform: `translate(${pos.x}px, ${pos.y}px)` }}>
      <button type="button" className="handle" aria-label="Flytta" title="Flytta"
        onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}>⋮⋮</button>
      <div className="row">
        <textarea rows={2} value={text.text} placeholder={t.typeText} onChange={(e) => onChange({ ...text, text: e.target.value })}
          onFocus={(e) => e.target.select()} />
        <button type="button" onClick={onDone}>{t.done}</button>
      </div>
      <details open={!small()}>
        <summary>{t.settings}</summary>
        <div className="row">
          <label>{t.font}
            <select value={text.font} onChange={(e) => onChange({ ...text, font: e.target.value })}>
              {FONTS.map((f) => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
            </select>
          </label>
          <label className="small"><input type="checkbox" checked={text.shadow} onChange={(e) => onChange({ ...text, shadow: e.target.checked })} /> {t.shadow}</label>
        </div>
        <div className="row swatches" title={t.color}>
          {PALETTE.map((c) => (
            <button key={c} type="button" className={c === text.color ? 'swatch on' : 'swatch'} style={{ background: c }}
              aria-label={c} onClick={() => onChange({ ...text, color: c })} />
          ))}
        </div>
        <div className="row">
          <label>{t.size}
            <input type="range" min={sizeMin} max={sizeMax} value={text.fontSizePx} onChange={(e) => onChange({ ...text, fontSizePx: Number(e.target.value) })} />
          </label>
        </div>
      </details>
    </div>
  );
}
